import type { Company } from '@/types/domain/company';

export interface CompanyAttendanceCapabilities {
  allowBookingWaitlist: boolean;
  allowScheduledBookings: boolean;
  allowWalkInQueue: boolean;
}

type CompanyAttendanceSource =
  | Pick<
      Company,
      'attendanceMode' | 'operationMode' | 'allowBookingWaitlist' | 'allowScheduledBookings' | 'allowWalkInQueue'
    >
  | null
  | undefined;

function resolveFallbackScheduledBookings(attendanceMode?: Company['attendanceMode']) {
  return attendanceMode !== 'WAITLIST_ONLY';
}

function resolveFallbackWalkInQueue(attendanceMode?: Company['attendanceMode']) {
  return attendanceMode === 'WAITLIST_ONLY';
}

function resolveFallbackBookingWaitlist(attendanceMode?: Company['attendanceMode']) {
  return attendanceMode === 'HYBRID';
}

function resolveOperationModeScheduledBookings(operationMode?: Company['operationMode']) {
  return operationMode !== 'QUEUE_ONLY';
}

function resolveOperationModeWalkInQueue(operationMode?: Company['operationMode']) {
  return operationMode === 'QUEUE_ONLY' || operationMode === 'HYBRID';
}

export function resolveCompanyAttendanceCapabilities(company?: CompanyAttendanceSource): CompanyAttendanceCapabilities {
  const allowScheduledBookings =
    typeof company?.allowScheduledBookings === 'boolean'
      ? company.allowScheduledBookings
      : company?.operationMode
        ? resolveOperationModeScheduledBookings(company.operationMode)
        : resolveFallbackScheduledBookings(company?.attendanceMode);
  const allowWalkInQueue =
    typeof company?.allowWalkInQueue === 'boolean'
      ? company.allowWalkInQueue
      : company?.operationMode
        ? resolveOperationModeWalkInQueue(company.operationMode)
        : resolveFallbackWalkInQueue(company?.attendanceMode);
  const allowBookingWaitlistBase =
    typeof company?.allowBookingWaitlist === 'boolean'
      ? company.allowBookingWaitlist
      : company?.operationMode
        ? false
        : resolveFallbackBookingWaitlist(company?.attendanceMode);

  return {
    allowBookingWaitlist: allowScheduledBookings ? allowBookingWaitlistBase : false,
    allowScheduledBookings,
    allowWalkInQueue,
  };
}
